import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import Button from '@adsk/bim360-matrix-react-button';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import IssueAttributesAdminAttributeLine from '../issue-attributes/IssueAttributesAdminAttributeLine';
import {sortAttributesBySelectedAndTitle} from '../../../utils/sortingHelpers';

class IssueTypesAddAttributesModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = this.resetState(props);

    this.onModalClose = this.onModalClose.bind(this);
    this.clearItems = this.clearItems.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onSearchClear = this.onSearchChange.bind(this, '');
    this.onCheckChange = this.onCheckChange.bind(this);
    this.createNewAttribute = this.createNewAttribute.bind(this);
    this.cancelClick = this.cancelClick.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.attributeDefinitions !== nextProps.attributeDefinitions) {
      this.setState(this.resetState(nextProps));
    }
  }

  resetState(props) {
    return {
      selectedItems: [],
      items: props.attributeDefinitions,
      searchExpression: ''
    };
  }

  createNewAttribute() {
    this.props.setSelectedIssueTypeDefinitionsAction(this.state.selectedItems);
    this.props.closeModal({shouldOpenLeaveAddAttributesModal: true});
  }

  sortBySelectedFirst(items, selectedItems) {
    return items.sort(sortAttributesBySelectedAndTitle.bind(this, selectedItems));
  }

  filterItems(searchExpression) {
    return this.props.attributeDefinitions.filter(item =>
      item.title.toLowerCase().includes(searchExpression.toLowerCase())
    );
  }

  onSearchChange(searchExpression) {
    // Allow to search for expressions with spaces
    const items =
      searchExpression.trim().length > 0 ? this.filterItems(searchExpression) : this.props.attributeDefinitions;
    this.setState({
      searchExpression,
      items: this.sortBySelectedFirst(items, this.state.selectedItems)
    });
  }

  onCheckChange(id) {
    const selectedItems = [].concat(this.state.selectedItems);
    const currentItemIndex = this.state.selectedItems.indexOf(id);
    if (currentItemIndex > -1) {
      selectedItems.splice(currentItemIndex, 1);
    } else {
      selectedItems.push(id);
    }
    this.setState({selectedItems});
  }

  clearItems() {
    this.setState({selectedItems: []});
  }

  cancelClick() {
    this.clearItems();
    this.onModalClose(false);
  }

  onModalClose(shouldSave = false) {
    if (shouldSave) {
      this.props.setSelectedIssueTypeDefinitionsAction(this.state.selectedItems);
    }
    this.props.closeModal();
  }

  _renderModalFooter() {
    return (
      <div className="IssueTypesAddAttributesModal__footer">
        <div className="IssueTypesAddAttributesModal__footer-left">
          {this.state.selectedItems.length > 0 && (
            <Button
              styleType={Button.StyleType.INLINE_LINK}
              onClick={this.clearItems}
              data-testid="IssueTypesAddAttributesModal__clear-items"
            >
              {i18n.t('issueTypes.modal.clearItems')}
            </Button>
          )}
        </div>
        <div className="IssueTypesAddAttributesModal__footer-right">
          <Button
            styleType={Button.StyleType.LINK_SECONDARY}
            onClick={this.cancelClick}
            className="IssueTypesAddAttributesModal__button IssueTypesAddAttributesModal__button--cancel"
            tabIndex="0"
            data-testid="IssueTypesAddAttributesModal__button--cancel"
          >
            {i18n.t('issueTypes.modal.cancel')}
          </Button>
          <Button
            styleType={Button.StyleType.PRIMARY}
            onClick={() => this.onModalClose(true)}
            className="IssueTypesAddAttributesModal__button"
            tabIndex="0"
            data-testid="IssueTypesAddAttributesModal__button--create"
          >
            {i18n.t('issueTypes.modal.add')}
          </Button>
        </div>
      </div>
    );
  }

  _renderAttribute(attribute) {
    const {id} = attribute;
    const isChecked = this.state.selectedItems.includes(id);
    return (
      <div
        className="IssueTypesAddAttributesModal__checkbox"
        data-testid="IssueTypesAddAttributesModal__checkbox"
        key={id}
      >
        <Checkbox checked={isChecked} onChange={this.onCheckChange.bind(this, id)} />
        <IssueAttributesAdminAttributeLine issueAttribute={attribute} container={this.props.container} />
      </div>
    );
  }

  _renderEmptyState() {
    const text = (
      <div className="IssueTypesAddAttributesModal__empty-state-text">
        <div className="IssueTypesAddAttributesModal__empty-state-text-line">
          {i18n.t('issueTypes.modal.noIssueAttributes')}
        </div>
        <div className="IssueTypesAddAttributesModal__empty-state-text-line">
          <Button
            data-testid="IssueTypesAddAttributesModal__empty-state-btn"
            styleType={Button.StyleType.INLINE_LINK}
            onClick={this.createNewAttribute}
          >
            {i18n.t('issueTypes.modal.createAttribute')}
          </Button>
          <div>{i18n.t('issueTypes.modal.inAttributesTab')}</div>
        </div>
      </div>
    );

    return (
      <EmptyState
        icon={EmptyState.Icons.EMPTY_BOX}
        className="IssueTypesAddAttributesModal__empty-state"
        text=""
        footer={text}
      />
    );
  }

  render() {
    return (
      <SimpleModal
        title={i18n.t('issueTypes.modal.addCustomAttributes')}
        show={this.props.isOpen}
        footer={this._renderModalFooter()}
        onHide={() => this.props.closeModal()}
        onExited={() => this.props.closeModal({isExited: true})}
        className="IssueTypesAddAttributesModal"
        container={this.props.container}
        modalBodyProps={{className: 'IssueTypesAddAttributesModal__modal-body'}}
      >
        <div className="IssueTypesAddAttributesModal__search-bar">
          <SearchBox
            placeholder={i18n.t('issueTypes.modal.findAnAttribute')}
            searchExpression={this.state.searchExpression}
            onChange={this.onSearchChange}
            onClear={this.onSearchClear}
            onSearch={() => {}}
            className="IssueTypesAddAttributesModal__search-box"
          />
          <Button onClick={this.createNewAttribute} data-testid="IssueTypesAddAttributesModal__create-new">
            {i18n.t('issueTypes.modal.createNew')}
          </Button>
        </div>
        <div className="IssueTypesAddAttributesModal__scrollable-container">
          {this.state.items.length > 0
            ? this.state.items.map(definition => this._renderAttribute(definition))
            : this._renderEmptyState()}
        </div>
      </SimpleModal>
    );
  }
}

IssueTypesAddAttributesModal.displayName = 'IssueTypesAddAttributesModal';

IssueTypesAddAttributesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  attributeDefinitions: PropTypes.arrayOf(PropTypes.object).isRequired,
  container: PropTypes.object.isRequired,
  setSelectedIssueTypeDefinitionsAction: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
};

export default IssueTypesAddAttributesModal;
